/**
 * useMapStore — Zustand-стор для нового /map (NDVI redesign v1.3).
 *
 * Хранит ВСЁ состояние карты (mode, дата, focus crop, toggle'ы, позиции
 * draggable панелей) в едином сторе и персистит в localStorage. Это позволяет:
 *   - reload страницы → панели остаются на местах,
 *   - переключение режимов → instant (state, не DOM),
 *   - sync hooks (useNdviModeSync, useDateSync) подписываются и применяют
 *     side-effects к MapLibre instance ОДИН раз на mount + per state change.
 *
 * Spec: docs/superpowers/specs/2026-04-30-ndvi-redesign-v1.md (v1.3).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NdviMode = 'none' | 'ndvi' | 'ndvi_contrast' | 'avg_ndvi' | 'uniformity' | 'gnr';

export type PanelId = 'crops' | 'ai' | 'satellites';

export interface PanelPosition {
  x: number;
  y: number;
}

export interface CompareMode {
  enabled: boolean;
  dateA: string | null;
  dateB: string | null;
}

/** Тип базовой подложки. По founder-фидбеку — три режима. */
export type Basemap = 'satellite' | 'hybrid' | 'streets';

/** Scope filter — на какой объект сейчас сфокусирована карта.
 *  Sprint 2026-23 T-A6: расширено до canonical 8 для соответствия
 *  mapState.ts (V2 store) ScopeType. Legacy V1 store (этот файл)
 *  пока используется в MapCanvas.tsx; union должен быть тот же что
 *  и у V2, иначе scopeEvent payload не присваивается. */
export type ScopeType =
  | 'tenant'
  | 'holding'
  | 'company'
  | 'cluster'
  | 'farm'
  | 'brigade'
  | 'array'
  | 'field';
export interface ActiveScope {
  type: ScopeType;
  id: string | null; // null для company
}

interface MapState {
  /** Mutex: какой analytics raster/fill сейчас активен. */
  mode: NdviMode;
  /** Последний non-'none' mode — используется для restore после collapse. */
  lastNdviMode: NdviMode;
  /** ISO YYYY-MM-DD — selected snapshot date. */
  selectedDate: string;
  /** Базовая подложка: satellite (только спутник) / hybrid (спутник + дороги) / streets (карта). */
  basemap: Basemap;
  /** Если задано — focus mode: только эта культура яркая, остальные приглушены. */
  focusedCropId: string | null;
  /** Toggle "Подписи: иконка культуры" (PNG-иконка в центре поля). */
  cropIconsOn: boolean;
  /** Toggle "Подписи: название культуры" (текст с crop_name). */
  cropNameOn: boolean;
  /** Toggle "Подписи: номер" (текст с field_number). */
  fieldNumberOn: boolean;
  /** Toggle "Подписи: площадь поля" (текст с area_ha, округлённое). */
  fieldAreaOn: boolean;
  /** Toggle "По цвету культуры" — единый toggle (founder 2026-05-04 v6).
   *  Цвет полигона берётся из культуры, штриховка зависит от статуса:
   *    - planned   → диагональные полосы цвета культуры
   *    - growing   → сплошная заливка цветом культуры
   *    - harvested → точки цвета культуры
   *  Раньше было два отдельных toggle (cropColorFillOn + phaseFillOn) — founder
   *  потребовал «селектор один — по цвету культуры, но цвет и штриховка зависят
   *  от статуса». */
  cropColorFillOn: boolean;
  /** Debug toggle — TCI snapshot raster. */
  showTCI: boolean;
  /** Debug toggle — cloud mask raster. */
  showCloudMask: boolean;
  /** Открыт ли drawer калибровки (правый Sheet). */
  calibrationOpen: boolean;
  /** Compare mode (split-screen двух дат). */
  compareMode: CompareMode;
  /** Позиции 3х draggable panels (persisted). */
  panelPositions: Record<PanelId, PanelPosition>;
  /** v2.4 (AK-73): свёрнута ли SatellitesPanel — если true, рендерится restore-pill. */
  satellitesPanelCollapsed: boolean;
  /** v2.4 (AK-73): свободная позиция SatellitesPanel (px от top-left map container).
   *  null = default bottom-center (CSS-positioned, не translate). */
  satellitesPanelPosition: PanelPosition | null;
  /** Selected field — для FieldDetailPopover. */
  selectedFieldId: string | null;
  /** Активный scope (компания/кластер/хозяйство). Меняется при клике по чипу
   * или из переключателя «Хозяйство ▾» в сайдбаре (`FarmSwitcher`). Влияет на
   * MVT tile URL (cluster_id/farm_id). */
  activeScope: ActiveScope;

  // ---------- Setters ----------
  setMode: (m: NdviMode) => void;
  setSelectedDate: (d: string) => void;
  setBasemap: (b: Basemap) => void;
  setFocusedCropId: (id: string | null) => void;
  setCropIconsOn: (on: boolean) => void;
  setCropNameOn: (on: boolean) => void;
  setFieldNumberOn: (on: boolean) => void;
  setFieldAreaOn: (on: boolean) => void;
  setCropColorFillOn: (on: boolean) => void;
  setShowTCI: (on: boolean) => void;
  setShowCloudMask: (on: boolean) => void;
  setCalibrationOpen: (on: boolean) => void;
  setCompareMode: (m: CompareMode) => void;
  setPanelPosition: (id: PanelId, pos: PanelPosition) => void;
  setSatellitesPanelCollapsed: (on: boolean) => void;
  setSatellitesPanelPosition: (pos: PanelPosition | null) => void;
  setSelectedFieldId: (id: string | null) => void;
  setActiveScope: (s: ActiveScope) => void;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** Default panel positions (px from top-left of map container). */
const DEFAULT_POSITIONS: Record<PanelId, PanelPosition> = {
  crops: { x: 24, y: 24 },
  ai: { x: 1080, y: 24 },
  satellites: { x: 110, y: 640 },
};

export const useMapStore = create<MapState>()(
  persist(
    (set) => ({
      // Default 'none' — Legend / NDVI растры скрыты, пока юзер не выберет
      // продукт в SatellitesPanel. Раньше был 'ndvi_contrast' — Legend
      // показывался всегда, даже если на staging нет залитых растров
      // (founder feedback 2026-05-02 «спутникового мониторинга вообще нет»).
      mode: 'none',
      lastNdviMode: 'ndvi',
      selectedDate: todayIso(),
      basemap: 'hybrid',
      focusedCropId: null,
      cropIconsOn: true,
      cropNameOn: false,
      fieldNumberOn: true,
      fieldAreaOn: false,
      cropColorFillOn: true,
      showTCI: false,
      showCloudMask: false,
      calibrationOpen: false,
      compareMode: { enabled: false, dateA: null, dateB: null },
      panelPositions: DEFAULT_POSITIONS,
      satellitesPanelCollapsed: false,
      satellitesPanelPosition: null,
      selectedFieldId: null,
      activeScope: { type: 'company', id: null },

      setMode: (mode) =>
        set((state) => ({
          mode,
          // Запоминаем последний non-'none' mode — для restore из collapsed state.
          lastNdviMode: mode !== 'none' ? mode : state.lastNdviMode,
        })),
      setSelectedDate: (selectedDate) => set({ selectedDate }),
      setBasemap: (basemap) => set({ basemap }),
      setFocusedCropId: (focusedCropId) => set({ focusedCropId }),
      setCropIconsOn: (cropIconsOn) => set({ cropIconsOn }),
      setCropNameOn: (cropNameOn) => set({ cropNameOn }),
      setFieldNumberOn: (fieldNumberOn) => set({ fieldNumberOn }),
      setFieldAreaOn: (fieldAreaOn) => set({ fieldAreaOn }),
      // v6 (2026-05-04): единый toggle «По цвету культуры». Управляет visibility
      // всех 3х phase-layers сразу (planned / growing / harvested) — каждый
      // рисует цвет культуры со своим pattern (полосы / solid / точки).
      setCropColorFillOn: (cropColorFillOn) => set({ cropColorFillOn }),
      setShowTCI: (showTCI) => set({ showTCI }),
      setShowCloudMask: (showCloudMask) => set({ showCloudMask }),
      setCalibrationOpen: (calibrationOpen) => set({ calibrationOpen }),
      setCompareMode: (compareMode) => set({ compareMode }),
      setPanelPosition: (id, pos) =>
        set((state) => ({
          panelPositions: { ...state.panelPositions, [id]: pos },
        })),
      setSatellitesPanelCollapsed: (satellitesPanelCollapsed) =>
        set({ satellitesPanelCollapsed }),
      setSatellitesPanelPosition: (satellitesPanelPosition) =>
        set({ satellitesPanelPosition }),
      setSelectedFieldId: (selectedFieldId) => set({ selectedFieldId }),
      setActiveScope: (activeScope) => set({ activeScope }),
    }),
    {
      name: 'ikolhoz-map-state',
      // v3 (2026-05-02): полный сброс mode на 'none' для всех старых юзеров
      //   (раньше migrate сбрасывал только 'ndvi_contrast' — но founder сидел
      //   на 'avg_ndvi' и Legend всё равно показывался).
      // + cropIconsOn default true → культуры видны без явного toggle.
      // v4 (2026-05-02, AK-73): добавлены satellitesPanelCollapsed,
      //   satellitesPanelPosition, lastNdviMode для drag/collapse фичи v2.4.
      // v5 (2026-05-04, founder feedback): 6 toggles вместо 2 для подписей+заливки —
      //   cropNameOn, fieldAreaOn, cropColorFillOn (default ON, как раньше выглядела
      //   карта), phaseFillOn (default OFF — phase pattern был mutex'ом со scope).
      // v6 (2026-05-04, founder feedback вторая итерация): unified «По цвету культуры»
      //   toggle. phaseFillOn удалён — оба фактора (цвет = культура, штриховка = статус)
      //   теперь объединены в одном cropColorFillOn (планируемые → полосы, растущие →
      //   solid, убранные → точки). Mutex с status-only режимом убран.
      version: 6,
      migrate: (persisted: unknown, fromVersion: number) => {
        if (!persisted || typeof persisted !== 'object') return persisted;
        const state = persisted as Record<string, unknown>;
        if (fromVersion < 3) {
          // Сбрасываем mode полностью — пользователь должен сам выбрать продукт.
          state.mode = 'none';
          // Включаем иконки культур на полях (были off).
          state.cropIconsOn = true;
        }
        if (fromVersion < 4) {
          state.satellitesPanelCollapsed = false;
          state.satellitesPanelPosition = null;
          state.lastNdviMode = 'ndvi';
        }
        if (fromVersion < 5) {
          // Defaults для новых toggles. Старые юзеры приходят с cropIconsOn=true
          // и fieldNumberOn=true — эти не трогаем.
          state.cropNameOn = false;
          state.fieldAreaOn = false;
          state.cropColorFillOn = true;
        }
        if (fromVersion < 6) {
          // phaseFillOn удалён — выкидываем поле, если оно есть.
          // cropColorFillOn становится unified toggle (наследуем как было).
          delete state.phaseFillOn;
          if (typeof state.cropColorFillOn !== 'boolean') {
            state.cropColorFillOn = true;
          }
        }
        return state;
      },
      // Не персистим выбранное поле и compare mode — это ephemeral session state.
      partialize: (s) => ({
        mode: s.mode,
        lastNdviMode: s.lastNdviMode,
        selectedDate: s.selectedDate,
        basemap: s.basemap,
        focusedCropId: s.focusedCropId,
        cropIconsOn: s.cropIconsOn,
        cropNameOn: s.cropNameOn,
        fieldNumberOn: s.fieldNumberOn,
        fieldAreaOn: s.fieldAreaOn,
        cropColorFillOn: s.cropColorFillOn,
        showTCI: s.showTCI,
        showCloudMask: s.showCloudMask,
        panelPositions: s.panelPositions,
        satellitesPanelCollapsed: s.satellitesPanelCollapsed,
        satellitesPanelPosition: s.satellitesPanelPosition,
        // Founder bug 2026-05-04: после reload activeScope сбрасывался на
        // {type:'company', id:null} → MVT URL без farm_id → backend возвращал
        // 0 bytes → labels не показывались. TopBar показывал Руно (server-side
        // /scope/get), но frontend store не знал и MVT шёл без фильтра.
        // Persist activeScope чтобы reload сохранял scope.
        activeScope: s.activeScope,
      }),
    },
  ),
);
